import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

// Quick-pick uses the LAST pairs the admin actually saved (read from /api/config
// where they persist in MongoDB). Falls back to none if the admin hasn't used
// the bot yet. Nothing hardcoded.
export default function PairSelector({ state, onChange }) {
  const { t } = useI18n();
  const [draft, setDraft] = useState(state?.symbol || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    if (state?.symbol) setDraft(state.symbol);
  }, [state?.symbol]);

  useEffect(() => {
    // Pull every per-exchange symbol the admin has saved → de-duplicated list
    // for the quick-pick row. If none yet, the row is empty (admin enters
    // their pair manually).
    api.get("/config").then(({ data }) => {
      const seen = new Set();
      if (data?.symbol) seen.add(data.symbol);
      Object.values(data?.exchanges || {}).forEach((ex) => {
        if (ex?.symbol) seen.add(ex.symbol);
      });
      setRecent([...seen].slice(0, 6));
    }).catch(() => {});
  }, [state?.symbol]);

  const apply = async (sym) => {
    const cleaned = String(sym ?? draft ?? "").trim().toUpperCase();
    setErr("");
    if (!cleaned) {
      setErr(t("pairRequired") || "Pair is required");
      return;
    }
    setBusy(true);
    try {
      await api.post("/bot/pair", { symbol: cleaned });
      onChange?.();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mq-panel p-3" data-testid="pair-selector">
      <div className="flex flex-wrap items-center gap-3">
        <span className="mq-label">{t("pair") || "Trading pair"}</span>
        <input
          data-testid="pair-input"
          className="mq-input flex-1 min-w-[160px] max-w-[240px]"
          value={draft}
          onChange={(e) => { setDraft(e.target.value.toUpperCase()); setErr(""); }}
          onKeyDown={(e) => { if (e.key === "Enter") apply(); }}
          placeholder="e.g. LMCSWAP/USDT"
        />
        <button
          data-testid="pair-apply-button"
          className="mq-btn mq-btn-primary"
          onClick={() => apply()}
          disabled={busy}
        >
          {busy ? "…" : "Apply"}
        </button>
        {recent.length > 0 && (
          <div className="flex flex-wrap gap-1" data-testid="pair-recent">
            <span className="mq-label text-[var(--mq-text-3)]">recent:</span>
            {recent.map((p) => (
              <button
                key={p}
                data-testid={`pair-recent-${p}`}
                onClick={() => { setDraft(p); apply(p); }}
                disabled={busy}
                className={`mq-btn ${state?.symbol === p ? "mq-btn-primary" : ""}`}
                style={{ padding: "0.25rem 0.55rem", fontSize: "0.65rem" }}
              >
                {p}
              </button>
            ))}
          </div>
        )}
        <span className="mq-label text-[var(--mq-text-3)]" data-testid="pair-current">
          active: {state?.symbol || "—"}
        </span>
      </div>
      {err && (
        <div data-testid="pair-error" className="text-xs mt-2 pl-3 border-l-2"
          style={{ borderColor: "var(--mq-red)", color: "var(--mq-red)" }}>
          {err}
        </div>
      )}
    </div>
  );
}
